import { Tag } from 'antd'
import { AiCitationChip } from 'features/libra/stepComponents/GongAgentAutofill'
import { ZoneTabPrefillMeta } from 'features/libra/stepComponents/zoneAnalysis/zoneAnalysisApi'
import { completeApmCitation } from 'features/libra/stepComponents/zoneAnalysis/zoneTabAutofill'
import { FC } from 'react'
import styled from 'styled-components'

export const FieldStateTag = styled(Tag)`
  margin-inline-start: 6px;
  font-size: 10px;
  font-weight: 500;
  line-height: 18px;
  letter-spacing: normal;
  text-transform: none;
`

/**
 * Per-field AI provenance tag + citation chip, rendered inline next to each
 * questionnaire field by the zone panels. This is the ONE leaf the panels
 * import from the AI-prefill feature — kept in its own module so a panel does
 * not have to pull in the heavy orchestration components (banners, ticket-level
 * header actions) just to render a tag. See CLAUDE.md import-direction rule.
 */
export const AiPrefillFieldState: FC<{
  meta: ZoneTabPrefillMeta | undefined
  field: string
  /**
   * Whether the field currently holds no value. After a prefill run, an
   * empty field that the AI didn't own is labelled as needing input.
   */
  empty?: boolean
}> = ({ meta, field, empty }) => {
  if (!meta) return null
  const isFilled = !empty && meta.fields.includes(field)
  const needsInput = empty || meta.unfound.includes(field)
  if (!isFilled && !needsInput) return null

  // Older saved runs contain only the APM source marker. The same metadata
  // already carries the matched component, so complete the display context at
  // render too (not just at ingestion); no re-run is required to fix the tooltip.
  const displayCitation = completeApmCitation(
    meta.citations?.[field],
    meta.component_match,
    field
  )

  return (
    <>
      {displayCitation !== null && displayCitation !== undefined && (
        // Reuse the Data Gathering citation chip verbatim: it reads
        // llmValue.citation and click-navigates to the document + page.
        <AiCitationChip
          fieldValue={{
            value: field,
            llmValue: { value: field, citation: displayCitation },
          }}
        />
      )}
      <FieldStateTag color={isFilled ? 'blue' : 'gold'}>
        {isFilled ? 'AI suggested' : 'Needs input'}
      </FieldStateTag>
    </>
  )
}
